import { useState } from "react";
import { brl, parseMoney } from "@/lib/api";
import { Button, Input, Thumb } from "@/components/ui-kit";
import { ScanBarcode, Trash2, Minus, Plus, PauseCircle, Percent } from "lucide-react";

const ATALHOS = [["F2", "finalizar"], ["F4", "desconto"], ["F6", "guardar"], ["Esc", "limpar busca"]];

/**
 * O carrinho da venda — o que o vendedor confere e o que o cliente olha na tela.
 *
 * A lista cresce no fluxo da página, em qualquer tamanho de tela. Antes ela
 * tinha altura travada e scroll próprio no desktop, para o total caber ao lado
 * numa tela sem rolagem; o preço disso era um PDV que se desmontava em telas
 * mais baixas — carrinho e totais desenhados um por cima do outro, e a busca de
 * cliente reduzida a uma fatia de poucos pixels. Quem garante o total sempre à
 * vista agora é a coluna de fechamento, que é fixa e tem altura própria (ver
 * POS.js). O carrinho ficou livre para ser tão comprido quanto a venda for.
 */
/**
 * Abatimento de UMA linha.
 *
 * Fica escondido atrás de um botão porque a maioria das vendas não tem desconto
 * por item — um campo aberto em toda linha viraria ruído e alvo de clique
 * acidental. Aparece expandido quando já existe abatimento, para nunca haver
 * desconto invisível no carrinho.
 */
function ItemDiscount({ item, onChange }) {
  const [aberto, setAberto] = useState(item.discount > 0);
  const bruto = item.price * item.qty;
  const valor = Math.min(bruto, parseMoney(item.discountInput ?? item.discount ?? 0));
  const pct = bruto > 0 ? (valor / bruto) * 100 : 0;
  const excede = parseMoney(item.discountInput ?? 0) > bruto + 0.001;

  if (!aberto) {
    return (
      <Button variant="link" size="sm" icon={Percent} className="mt-1 h-6 text-xs"
        onClick={() => setAberto(true)} data-testid="pos-item-discount-open">
        Desconto
      </Button>
    );
  }

  return (
    <div className="mt-1.5" data-testid="pos-item-discount">
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-slate-500 shrink-0">Desconto R$</span>
        <Input inputMode="decimal" size="sm" mono
          value={item.discountInput ?? (item.discount ? String(item.discount) : "")}
          onChange={(e) => onChange(item.key, e.target.value)}
          aria-label={`Desconto em ${item.name}`} aria-invalid={excede || undefined}
          placeholder="0,00" data-testid="pos-item-discount-input"
          className={`w-24 px-2 ${excede ? "border-red-300" : ""}`} />
        {valor > 0 && !excede && (
          <span className="text-xs text-slate-400 whitespace-nowrap">
            {pct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
          </span>
        )}
      </div>
      {/* O erro é da LINHA, não da venda: o servidor recusa nomeando o produto,
          e descobrir isso só no clique de finalizar custaria a venda montada. */}
      {excede && (
        <p role="alert" className="text-xs text-red-600 mt-1" data-testid="pos-item-discount-error">
          Máximo {brl(bruto)} nesta linha.
        </p>
      )}
    </div>
  );
}

export default function CartList({ items, count, flashKey, onChangeQty, onRemove, onChangeDiscount, heldCount, onOpenHeld }) {
  return (
    <section className="flex flex-col bg-white border border-slate-200 rounded-xl">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-2 shrink-0">
        <span className="font-semibold text-slate-900">Venda atual</span>
        <div className="flex items-center gap-3">
          {items.length > 0 && (
            <span className="text-xs text-slate-400 font-mono" data-testid="pos-cart-count">
              {count} {count === 1 ? "item" : "itens"}
            </span>
          )}
          {/* As vendas em aberto ficam no cabeçalho do carrinho: é onde o
              vendedor já está olhando quando o cliente volta para fechar. */}
          <Button variant="ghost" size="sm" icon={PauseCircle} onClick={onOpenHeld}
            data-testid="pos-held-open" className={heldCount ? "text-brand-600" : ""}>
            Em aberto{heldCount ? ` (${heldCount})` : ""}
          </Button>
        </div>
      </div>

      <div className="divide-y divide-slate-100">
        {items.length === 0 && (
          <div className="p-8 text-center" data-testid="pos-cart-empty">
            <ScanBarcode size={30} className="mx-auto text-slate-300 mb-3" aria-hidden />
            <p className="text-sm font-medium text-slate-600">Nenhum item na venda</p>
            <p className="text-xs text-slate-400 mt-1">Escaneie um código de barras ou busque pelo nome acima.</p>
          </div>
        )}
        {items.map((i) => (
          /* Duas colunas: o nome tem uma linha só para ele e os controles têm
             largura reservada. Antes tudo dividia a mesma linha e o preço de 5
             dígitos passava por cima da lixeira. A quebra é em `sm` (e não `xl`)
             porque o aperto começa muito antes do desktop. */
          <div key={i.key}
            className={`grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-x-3 gap-y-2 sm:items-center px-4 py-3 transition-colors ${flashKey === i.key ? "bg-emerald-50" : ""}`}
            data-testid="pos-cart-item">
            <div className="min-w-0 flex items-start gap-3">
              {/* Linha vinda de venda em espera antiga ou de recompra pode não
                  ter foto; o Thumb desenha o quadradinho neutro no lugar. */}
              <Thumb src={i.image} alt="" iconSize={14}
                className="w-10 h-10 rounded-md border border-slate-100 object-contain bg-white shrink-0" />
              <div className="min-w-0">
                <p className="font-medium text-slate-900 leading-snug">{i.name}</p>
                {/* Marca junto da variação: é o que o cliente confere na tela
                    antes de fechar ("é a Mercur mesmo?"). */}
                <p className="text-xs text-slate-500 mt-0.5">
                  {i.brand ? `${i.brand} · ` : ""}{i.variant_label} · {brl(i.price)} un.
                </p>
                <ItemDiscount item={i} onChange={onChangeDiscount} />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 sm:gap-3 shrink-0">
              {/* 40px de alvo: é botão que se aperta com pressa, muitas vezes
                  seguidas, e às vezes com luva. */}
              <div className="flex items-center border border-slate-200 rounded-lg">
                <button type="button" onClick={() => onChangeQty(i.key, -1)} data-testid="pos-qty-minus"
                  aria-label={`Diminuir ${i.name}`}
                  className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-brand-600"><Minus size={14} /></button>
                <span className="text-sm font-mono tabular-nums w-7 text-center" data-testid="pos-qty">{i.qty}</span>
                <button type="button" onClick={() => onChangeQty(i.key, 1)} data-testid="pos-qty-plus"
                  aria-label={`Aumentar ${i.name}`}
                  className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-brand-600"><Plus size={14} /></button>
              </div>
              {/* Com abatimento, o bruto sai riscado acima do líquido: o cliente
                  confere na tela o que foi combinado, e não só o valor final. */}
              <span className="min-w-[5.5rem] sm:min-w-[6.5rem] text-right">
                {i.discount > 0 && (
                  <span className="block text-xs text-slate-400 line-through font-mono tabular-nums">
                    {brl(i.price * i.qty)}
                  </span>
                )}
                <span className="font-mono tabular-nums font-semibold text-slate-900">
                  {brl(Math.max(0, i.price * i.qty - (i.discount || 0)))}
                </span>
              </span>
              <Button variant="ghost" size="iconSm" onClick={() => onRemove(i.key)}
                data-testid="pos-cart-remove" aria-label={`Remover ${i.name}`} className="hover:text-red-600"><Trash2 size={15} /></Button>
            </div>
          </div>
        ))}
      </div>

      {/* Atalhos: só onde existe teclado. No celular a linha era ruído puro. */}
      <div className="hidden lg:flex px-4 py-2.5 border-t border-slate-100 flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400 shrink-0">
        {ATALHOS.map(([tecla, acao]) => (
          <span key={tecla}><kbd className="font-mono font-semibold text-slate-500">{tecla}</kbd> {acao}</span>
        ))}
      </div>
    </section>
  );
}
